import { ReactNode, createContext, useState, useMemo, useContext, useEffect, useRef } from 'react'
import { Clickable } from 'components/common/Clickable';
import { CloseIcon } from 'components/common/Icons';

import styles from './styles.module.css';

type Props = {
    title?: string
    children?: ReactNode
}

type Dispatcher = {
    show: (props: Props) => void
    close: () => void
}

const defaultDispatcher = {
    show: () => {},
    close: () => {}
}

const PopupContext = createContext<Dispatcher>(defaultDispatcher)

export const PopupProvider: React.FC<{ children: ReactNode }> = ({children}) => {
    const [state, setState] = useState<Props | undefined>(undefined)
    const dispatcher = useMemo(() => ({
        show: (props: Props) => { setState(props) },
        close: () => { setState(undefined) }
    }), [])

    return (
        <PopupContext.Provider value={dispatcher}>
            <PopupContainer props={state} onClose={dispatcher.close}>
                {children}
            </PopupContainer>
        </PopupContext.Provider>
    )
}

const PopupContainer: React.FC<{
    props?: Props
    children?: ReactNode,
    onClose: () => void
}> = ({props, children, onClose}) => (
    <div className={styles.root}>
        {props && <Popup props={props} onClose={onClose} /> }
        {children}
    </div>
)

const Popup: React.FC<{
    props: Props,
    onClose: () => void
}> = ({props, onClose}) => {
    const contentRef = useRef<HTMLDivElement>(null)
    useEffect(() => {
        const callback = (e: KeyboardEvent) => {
            if (e.code === 'Escape') {
                onClose()
            }
        }
        document.addEventListener('keydown', callback)
        return () => {
            document.removeEventListener('keydown', callback)
        }
    }, [onClose])
    useEffect(() => {
        contentRef.current?.focus()
    }, [])
    return (
        <div className={styles.container} onClick={onClose}>
            <div className={styles.popup} onClick={(e) => e.stopPropagation()}>
                {props.title && <h1>{props.title}</h1>}
                <Clickable className={styles.close} onClick={onClose} type='item'>
                    <CloseIcon />
                </Clickable>
                <div ref={contentRef} tabIndex={0} className={styles.content}>
                    {props.children}
                </div>
            </div>
        </div>
    )
}

export const usePopupDispatcher: () => Dispatcher = () => {
    return useContext(PopupContext)
}
